import {
  createScoutAction,
  createScoutPage,
  createScoutSection,
} from "scoutbar";
import {
  AiOutlineAppstoreAdd,
  AiOutlineFileSearch,
  AiOutlineTeam,
  AiOutlineUsergroupAdd,
  AiOutlineShopping,
  AiOutlineDatabase,
  AiOutlineRobot,
} from "react-icons/ai";
import { useRouter } from "next/router";
import GlobeIcon from "../Icons/GlobeIcon";
import useProjects from "./useProjects";
import { RiTerminalFill } from "react-icons/ri";
import { HiOutlineSupport, HiOutlineSwitchHorizontal } from "react-icons/hi";
import useTeams from "./useTeams";
import useIsTeam from "./useIsTeam";
import useFrameworks from "./useFrameworks";
import Image from "next/image";
import { MessageCircleDashed } from "lucide-react";

const useActions = () => {
  const router = useRouter();
  const { projects } = useProjects({ useStore: true });
  const { teams } = useTeams();
  const { isTeam, team } = useIsTeam();
  const { data } = useFrameworks();

  const createProjectAction = createScoutAction({
    label: "New Project",
    description: "Create a new project",
    action: () =>
      router.push(
        isTeam
          ? `/teams/${team}/projects/new?stage=provider`
          : "/projects/new?stage=provider",
      ),
    keyboardShortcut: ["N"],
    icon: <AiOutlineAppstoreAdd />,
  });

  const projectListAction =
    projects?.projects?.map((project: any) => {
      const framework = data?.find((f) => f.slug === project.framework);
      return createScoutAction({
        label: project.name,
        description: project.description || project?.log?.message,
        action: () =>
          router.push(
            isTeam
              ? `/teams/${team}/projects/${project.name}`
              : `/projects/${project.name}`,
          ),
        icon: (
          <div className="mr-2 flex items-center">
            <Image
              src={project?.dbImage?.image_url || framework?.logo || ""}
              alt={framework?.slug}
              width={25}
              height={25}
              className="rounded-full"
            />
          </div>
        ),
      });
    }) || [];

  const projectSearchPage = createScoutPage({
    label: "Search Projects",
    children: [...projectListAction],
    icon: <AiOutlineFileSearch />,
  });

  const projectSection = createScoutSection({
    label: "Projects",
    children: [createProjectAction, projectSearchPage],
  });

  const teamListAction =
    teams.map((team: any) => {
      return createScoutAction({
        label: team.name.charAt(0).toUpperCase() + team.name.slice(1),
        description: team.description,
        action: () => router.push(`/teams/${team.name}`),
        icon: <AiOutlineTeam />,
      });
    }) || [];

  const teamSearchPage = createScoutPage({
    label: "Switch Scope To Team",
    children: [...teamListAction],
    icon: <HiOutlineSwitchHorizontal />,
  });

  const teamSection = createScoutSection({
    label: "Teams",
    children: [
      createScoutAction({
        label: "New Team",
        description: "Create a new team",
        action: () => router.push("/teams/new"),
        keyboardShortcut: ["T"],
        icon: <AiOutlineUsergroupAdd />,
      }),
      teamSearchPage,
    ],
  });

  const domainSection = createScoutSection({
    label: "Domains",
    children: [
      createScoutAction({
        label: "New Domain",
        description: "Create a new domain",
        action: () =>
          router.push(
            isTeam ? `/teams/${team}/domains/register` : "/domains/register",
          ), //"/domains/register"
        keyboardShortcut: ["D"],
        icon: <GlobeIcon />,
      }),
      createScoutAction({
        label: "Buy Domain",
        description: "Purchase a new domain for your project",
        action: () => router.push("/domains/new"),
        keyboardShortcut: ["B"],
        icon: <AiOutlineShopping />,
      }),
    ],
  });

  const helpSection = createScoutSection({
    label: "Help",
    children: [
      createScoutAction({
        label: "CLI Docs",
        description: "Need help with the CLI?",
        action: () => window.open("https://docs.brimble.io", "_blank"),
        icon: <RiTerminalFill />,
      }),
      createScoutAction({
        label: "Contact Support",
        description: "Reach out to our support team",
        action: () => router.push(`mailto:hello@brimble.app`),
        icon: <HiOutlineSupport />,
      }),
    ],
  });

  const databaseSection = createScoutSection({
    label: "Databases",
    children: [
      createScoutAction({
        label: "New Database",
        description: "Deploy a new database",
        action: () =>
          router.push(
            isTeam
              ? `/teams/${team}/projects/new/database"`
              : "/projects/new/database",
          ),
        keyboardShortcut: ["D", "B"],
        icon: <AiOutlineDatabase />,
      }),
    ],
  });

  const mcpSection = createScoutSection({
    label: "AI",
    children: [
      createScoutAction({
        label: "Mcp Servers",
        description: "Explore brimble mcp marketplace",
        action: () => router.push("/mcp-servers"),
        keyboardShortcut: ["C", "P"],
        icon: <MessageCircleDashed />,
      }),
      createScoutAction({
        label: "Brimble Nyph",
        description: "Access powerful LLMs with just one API Key",
        action: () => window.open("https://nyph-docs.brimble.io", "_blank"),
        icon: <AiOutlineRobot />,
      }),
    ],
  });

  return [
    mcpSection,
    projectSection,
    databaseSection,
    domainSection,
    teamSection,
    helpSection,
  ];
};

export default useActions;
